import { Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { IAccount, UserService } from '@shared/services/user.service';
import { catchError, filter, of, Subject, Subscription, switchMap, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserToNameCacheService {
	constructor(private userService: UserService) {
		this.initCache();
	}

	private readonly _isInitializing: WritableSignal<boolean> = signal(true);
	public readonly isInitializing: Signal<boolean> = this._isInitializing;

	private readonly _isLoading: WritableSignal<boolean> = signal(false);
	public readonly isLoading: Signal<boolean> = this._isLoading;

	private readonly _userCache: WritableSignal<Map<string, string>> = signal(new Map<string, string>());
	public readonly userCache: Signal<Map<string, string>> = this._userCache;

	private readonly _subscription: WritableSignal<Subscription | null> = signal(null);

	private readonly changesSubject: Subject<void> = new Subject<void>();
	public readonly changes$ = this.changesSubject.asObservable();

	destroy(): void {
		this._subscription()?.unsubscribe();
		this.changesSubject.complete();
	}

	initCache(): void {
		// If already initialized or currently loading, do nothing
		if (this._isInitializing() === false || this._isLoading() === true) {
			return;
		}

		this._userCache.set(new Map<string, string>());

		const subscription = this.userService.account$
			.pipe(
				filter((account): account is IAccount => !!account),
				tap(() => {
					this._isLoading.set(true);
					this._userCache.set(new Map<string, string>());
					this.changesSubject.next();
				}),
				switchMap((account) => this.userService.fetchUsers(account).pipe(catchError(() => of([])))),
			)
			.subscribe((users) => {
				const newUsers = new Map<string, string>(users.map((user) => [user.id, user.name ?? user.email] as [string, string]));

				this._userCache.set(newUsers);
				this.changesSubject.next();
				this._isLoading.set(false);
				this._isInitializing.set(false);
			});

		this._subscription.set(subscription);
	}

	/** Safe accessor for the cached name by id. Returns undefined if not found. */
	getName(id: string): string | undefined {
		return this._userCache().get(id);
	}
}
